import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import { MaskedLines } from "@/components/Reveal";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { SUBCATEGORIAS_CELULARES } from "@/lib/format";

const filters = [
  { id: "todos", label: "Todos" },
  { id: "celulares", label: "Celulares" },
  { id: "acessorios", label: "Acessórios" },
  { id: "telas", label: "Telas" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const categoria = params.get("categoria") || "todos";
  const sub = params.get("sub") || "";
  const [products, setProducts] = useState(null);

  useEffect(() => {
    setProducts(null);
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, []);

  const filtered =
    products === null
      ? null
      : categoria === "todos"
        ? products
        : products
            .filter((p) => p.category === categoria)
            .filter((p) => categoria !== "celulares" || !sub || (p.subcategory || "") === sub);

  return (
    <div className="bg-ink min-h-screen">
      <div className="mx-auto max-w-7xl px-5 lg:px-10 pt-32 md:pt-36 pb-20">
        <p className="flex items-center gap-3 text-cyan text-xs uppercase tracking-[0.22em] mb-5">
          <span className="h-px w-10 bg-cyan inline-block" />
          Catálogo completo
        </p>
        <h1 className="font-display font-semibold tracking-tight leading-[0.95] text-5xl md:text-7xl">
          <MaskedLines lines={["Escolha", <><span className="text-cyan">o seu.</span></>]} />
        </h1>

        <div className="flex flex-wrap items-center gap-2.5 mt-10" data-testid="shop-filters">
          {filters.map((f) => {
            const active = categoria === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setParams(f.id === "todos" ? {} : { categoria: f.id })}
                className={`h-10 px-5 rounded-md text-sm transition-all duration-300 border ${
                  active
                    ? "bg-cyan text-ink font-semibold border-cyan"
                    : "border-white/15 text-mute hover:text-white hover:border-white/40"
                }`}
                data-testid={`filter-pill-${f.id}`}
              >
                {f.label}
              </button>
            );
          })}
          {filtered && (
            <span className="text-xs text-mute ml-auto" data-testid="shop-count">
              {filtered.length} {filtered.length === 1 ? "produto" : "produtos"}
            </span>
          )}
        </div>

        {categoria === "celulares" && (
          <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="shop-subfilters">
            <button
              onClick={() => setParams({ categoria: "celulares" })}
              className={`h-8 px-3.5 rounded-md text-xs border transition-all duration-300 ${
                !sub
                  ? "bg-white/10 text-white border-white/40"
                  : "border-white/10 text-mute hover:text-white hover:border-white/30"
              }`}
              data-testid="subfilter-all"
            >
              Todas as linhas
            </button>
            {SUBCATEGORIAS_CELULARES.map((s) => {
              const active = sub === s;
              const n =
                products === null
                  ? 0
                  : products.filter((p) => p.category === "celulares" && (p.subcategory || "") === s).length;
              return (
                <button
                  key={s}
                  onClick={() => setParams({ categoria: "celulares", sub: s })}
                  className={`h-8 px-3.5 rounded-md text-xs border transition-all duration-300 ${
                    active
                      ? "bg-white/10 text-cyan border-cyan/50"
                      : "border-white/10 text-mute hover:text-white hover:border-white/30"
                  }`}
                  data-testid={`subfilter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {s}
                  {products !== null && ` (${n})`}
                </button>
              );
            })}
          </div>
        )}

        {filtered === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-10">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-[420px] bg-elevated border border-white/5 rounded-lg" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 text-center py-20 border border-dashed border-white/15 rounded-lg">
            <p className="font-display text-2xl">Nada por aqui ainda</p>
            <p className="text-mute text-sm mt-2">
              Nenhum produto nesta categoria. Volte em breve ou escolha outra
              categoria.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-10">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
